/**
 * cleanContentUrl.js
 *
 * Kullanıcının yapıştırdığı müzik/video linklerini doğrular ve takip/kimlik
 * parametrelerinden arındırır.
 *
 * Kullanım:
 *   import { cleanContentUrl } from '@/utils/cleanContentUrl';
 *   const { isValid, url, error, platform } = cleanContentUrl(input);
 *
 * Dönüş:
 *   { isValid: true,  url: 'https://...', platform: 'youtube' | 'youtube-music' | 'spotify' | 'other', error: null }
 *   { isValid: false, url: null,          platform: ...,                                              error: 'Türkçe hata mesajı' }
 */

// Tüm platformlarda silinecek takip / kimlik parametreleri
const TRACKING_PARAM_RE =
  /^(utm_.+|si|ref|ref_src|ref_url|fbclid|gclid|dclid|msclkid|yclid|twclid|ttclid|igshid|igsh|mc_cid|mc_eid|_ga|_gl|spm|feature|pp|uo)$/i;

const YT_HOSTS = new Set(['youtube.com', 'm.youtube.com', 'music.youtube.com', 'youtu.be']);
const SPOTIFY_TYPES = new Set(['track', 'album', 'playlist', 'artist', 'episode', 'show']);

const MSG = {
  format: 'Geçerli bir bağlantı girin (http veya https ile başlayan).',
  root: 'Ana sayfa bağlantıları kabul edilmez. Bir şarkı, video veya liste bağlantısı yapıştırın.',
  ytNoVideo: 'YouTube bağlantısında video veya liste kimliği bulunamadı.',
  ytBadId: 'YouTube video kimliği geçersiz görünüyor.',
  ytNoList: 'YouTube liste bağlantısında liste kimliği bulunamadı.',
  spotifyPath: 'Spotify bağlantısı bir şarkı, albüm, çalma listesi, sanatçı veya bölüm olmalı.',
};

const fail = (platform, error) => ({ isValid: false, url: null, platform, error });
const ok = (platform, url) => ({ isValid: true, url, platform, error: null });

function parseUrl(raw) {
  if (typeof raw !== 'string') return null;
  let s = raw.trim();
  if (!s || /\s/.test(s)) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) s = `https://${s}`;
  let u;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  return u;
}

const trimSlashes = (p) => p.replace(/\/+$/, '');

function cleanYouTube(u, host) {
  const platform = host === 'music.youtube.com' ? 'youtube-music' : 'youtube';
  const path = trimSlashes(u.pathname);
  if (!path) return fail(platform, MSG.root);

  const v = u.searchParams.get('v');
  const list = u.searchParams.get('list');

  // youtu.be/VIDEO_ID kısa linki: kimlik yolun içinde
  if (host === 'youtu.be') {
    const id = path.slice(1).split('/')[0];
    if (!/^[\w-]{11}$/.test(id)) return fail(platform, MSG.ytBadId);
    const out = new URL(`https://youtu.be/${id}`);
    if (list && /^[\w-]+$/.test(list)) out.searchParams.set('list', list);
    return ok(platform, out.toString());
  }

  if (path === '/watch' && !v && !list) return fail(platform, MSG.ytNoVideo);
  if (path === '/playlist' && !list) return fail(platform, MSG.ytNoList);
  if (v && !/^[\w-]{11}$/.test(v)) return fail(platform, MSG.ytBadId);

  const outHost = host === 'm.youtube.com' ? 'www.youtube.com' : host;
  const out = new URL(`https://${outHost}${path}`);
  // Yalnızca v ve list korunur; geri kalan her şey atılır
  if (v) out.searchParams.set('v', v);
  if (list && /^[\w-]+$/.test(list)) out.searchParams.set('list', list);
  return ok(platform, out.toString());
}

function cleanSpotify(u) {
  const segs = trimSlashes(u.pathname).split('/').filter(Boolean);
  if (segs.length === 0) return fail('spotify', MSG.root);

  // /intl-tr/track/... gibi dil önekini at
  if (/^intl-[a-z]{2}(-[a-z]{2})?$/i.test(segs[0])) segs.shift();

  const [type, id] = segs;
  if (!type || !SPOTIFY_TYPES.has(type) || !id || !/^[A-Za-z0-9]{10,30}$/.test(id)) {
    return fail('spotify', MSG.spotifyPath);
  }
  // Sorgu parametrelerinin tamamı (si dahil) atılır
  return ok('spotify', `https://open.spotify.com/${type}/${id}`);
}

function cleanGeneric(u) {
  const keys = [...u.searchParams.keys()].filter((k) => TRACKING_PARAM_RE.test(k));
  keys.forEach((k) => u.searchParams.delete(k));

  const path = trimSlashes(u.pathname);
  const search = u.searchParams.toString();
  if (!path && !search) return fail('other', MSG.root);

  // origin kullanıcı adı/şifre (user:pass@) kısmını da otomatik dışarıda bırakır
  return ok('other', `${u.origin}${u.pathname}${search ? `?${search}` : ''}`);
}

export function cleanContentUrl(rawUrl) {
  const u = parseUrl(rawUrl);
  if (!u) return fail('other', MSG.format);

  const host = u.hostname.replace(/^www\./, '').toLowerCase();

  if (YT_HOSTS.has(host)) return cleanYouTube(u, host);
  if (host === 'open.spotify.com') return cleanSpotify(u);
  return cleanGeneric(u);
}

export default cleanContentUrl;